import { Drawer } from "@base-ui/react/drawer";
import type * as React from "react";

const TEXT_ENTRY = 'input, textarea, select, [contenteditable]:not([contenteditable="false"])';

/**
 * Whether a drag that starts on `target` belongs to the control under the finger rather
 * than to the page: text entry (its drags select text), an element whose `touch-action`
 * leaves the browser no sideways pan because it takes its own drags (sliders, pickers,
 * drag handles), or a row that scrolls sideways. The walk stops at `page`, whose own
 * `touch-action` is the swipe's; content portaled out of it (a popover) walks to the body.
 */
function controlOwnsDrag(target: EventTarget, page: Element): boolean {
  for (
    let node = target instanceof Element ? target : null;
    node && node !== page && node !== node.ownerDocument.body;
    node = node.parentElement
  ) {
    if (node.matches(TEXT_ENTRY)) return true;
    const style = getComputedStyle(node);
    const touchAction = style.touchAction;
    if (
      touchAction !== "" &&
      touchAction !== "auto" &&
      touchAction !== "manipulation" &&
      !/\bpan-(x|left|right)\b/.test(touchAction)
    ) {
      return true;
    }
    if (
      (style.overflowX === "auto" || style.overflowX === "scroll") &&
      node.scrollWidth > node.clientWidth
    ) {
      return true;
    }
  }
  return false;
}

function leaveDragToControl(event: {
  target: EventTarget;
  currentTarget: Element;
  preventBaseUIHandler: () => void;
}): void {
  if (controlOwnsDrag(event.target, event.currentTarget)) event.preventBaseUIHandler();
}

/**
 * The phone sidebar's swipe area, inside its `Drawer.Root`: a rightward swipe anywhere on
 * the page opens the sidebar (Android's back gesture owns the edge). The area is boxless,
 * so taps and scrolling reach the page, and a drag that starts on a control that owns it
 * never reaches the swipe.
 */
export function SidebarSwipeArea({ children }: { children: React.ReactNode }) {
  return (
    <Drawer.SwipeArea
      onPointerDown={leaveDragToControl}
      onTouchStart={leaveDragToControl}
      // The area wraps the whole page: the attributes that hide an empty gesture surface from
      // assistive technology would hide the page with it.
      render={({ "aria-hidden": _hidden, role: _role, ...props }) => (
        <div {...props} className="contents" data-slot="sidebar-swipe-area" />
      )}
    >
      {children}
    </Drawer.SwipeArea>
  );
}
